import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebase';
import {
  matchTeamByPasscodeForEvent,
  TEAM_PASSCODE_MAP_NO_TEAM,
} from '../utils/matchTeamByPasscode';
import './JumpItOrFixItPuzzle.css';

const TEAM_CACHE_PREFIX = 'fm_jumpItTeam_';

function getCachedTeam(puzzleId) {
  try {
    const raw = window.sessionStorage.getItem(TEAM_CACHE_PREFIX + puzzleId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setCachedTeam(puzzleId, team) {
  try {
    window.sessionStorage.setItem(TEAM_CACHE_PREFIX + puzzleId, JSON.stringify(team));
  } catch {}
}

function progressDocId(eventId, teamId, puzzleId) {
  return `${eventId}_${teamId}_${puzzleId}`;
}

function buildSolvedWordFromStations(stations) {
  if (!Array.isArray(stations)) return '';
  return stations
    .filter((s) => s.letter && String(s.letter).trim() !== '' && s.letter !== 'Start')
    .map((s) => String(s.letter).trim())
    .join('');
}

const CRYPTEX_IMG_SRC = '/jumpitfixit/cryptex-manifest.png';

const JumpItCryptexPanel = ({ solvedWord }) => (
  <div className="jumpit-cryptex-panel">
    <h3 className="jumpit-cryptex-heading">Next step: manifest</h3>
    <p className="jumpit-cryptex-lead">Your word — enter it on the cryptex at manifest:</p>
    <p className="jumpit-cryptex-word" aria-live="polite">{solvedWord || '—'}</p>
    <p className="jumpit-cryptex-hint">
      The letters must match this order. Spin the rings on the cryptex to spell the word, then show manifest to check in.
    </p>
    <figure className="jumpit-cryptex-figure">
      <img
        src={CRYPTEX_IMG_SRC}
        alt="Red combination cryptex with rotating number rings — use this device at manifest"
        className="jumpit-cryptex-photo"
      />
      <figcaption className="jumpit-cryptex-caption">What the cryptex looks like</figcaption>
    </figure>
  </div>
);

const JumpItOrFixItPuzzle = ({ puzzle, activeEvent, stationId: stationIdProp }) => {
  const stations = useMemo(
    () => (Array.isArray(puzzle?.jumpItStations) ? puzzle.jumpItStations : []),
    [puzzle]
  );

  const stationId = stationIdProp || null;
  const station = useMemo(
    () => stations.find((s) => s.stationId === stationId) || null,
    [stations, stationId]
  );

  const [team, setTeam] = useState(() => getCachedTeam(puzzle?.id));
  const [passcodeInput, setPasscodeInput] = useState('');
  const [feedback, setFeedback] = useState('');
  const [progress, setProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Result of evaluating the current scan against the previous question.
  // Shape: { kind: 'first' | 'arrived-correct' | 'arrived-wrong' | 'wandered' | 'same', prev?, message? }
  const [scanResult, setScanResult] = useState(null);

  const loadProgress = useCallback(async () => {
    if (!team || !activeEvent || !puzzle) return;
    setProgressLoading(true);
    try {
      const id = progressDocId(activeEvent.id, team.id, puzzle.id);
      const snap = await getDoc(doc(firestore, 'jumpItProgress', id));
      setProgress(snap.exists() ? snap.data() : { correctStations: [], attempts: {}, completedAt: null, currentStationId: '' });
    } catch (err) {
      setFeedback('Could not load your team progress: ' + err.message);
    } finally {
      setProgressLoading(false);
    }
  }, [team, activeEvent, puzzle]);

  useEffect(() => {
    if (team && activeEvent && puzzle) loadProgress();
  }, [team, activeEvent, puzzle, loadProgress]);

  useEffect(() => {
    setScanResult(null);
    setFeedback('');
  }, [stationId]);

  // Stations lookup helpers
  const stationById = useCallback(
    (id) => (id ? stations.find((s) => s.stationId === id) || null : null),
    [stations]
  );
  const labelFor = (s) => (s ? (s.sponsor?.name?.trim() || s.name?.trim() || s.stationId) : '');

  // Track which scan we've already evaluated so we don't re-judge after the
  // Firestore write updates progress (which would otherwise flip the success
  // banner back to "same station").
  const evaluatedScanRef = useRef('');

  // Evaluate the scan: did the player arrive at the right destination from
  // their previous question? Updates progress in Firestore on correct/wrong.
  useEffect(() => {
    if (!team || !activeEvent || !puzzle || !station || progressLoading || !progress) return;
    if (submitting) return;
    if (progress.completedAt) return;
    // One evaluation per scanned stationId. Browser navigation to a new
    // station QR changes stationId → new eval. Refresh of the same URL =
    // no re-eval (user keeps seeing the success banner).
    if (evaluatedScanRef.current === stationId) return;
    evaluatedScanRef.current = stationId;
    const prevId = progress.currentStationId || '';

    // Determine what to show — synchronously, BEFORE any async work, so the
    // wrong / wandered banner appears in the same render and we don't flash
    // the "next question" screen for one frame.
    const prev = prevId ? stationById(prevId) : null;
    let resolved;
    let needsWrite = false;
    let writeKind = null; // 'first' | 'correct' | 'wrong'

    if (!prevId) {
      resolved = { kind: 'first' };
      needsWrite = true;
      writeKind = 'first';
    } else if (prevId === station.stationId) {
      resolved = { kind: 'same' };
    } else if (!prev) {
      resolved = { kind: 'first' };
      needsWrite = true;
      writeKind = 'first';
    } else {
      let derived = null;
      if (station.stationId === prev.jumpDestinationStationId) derived = 'jump';
      else if (station.stationId === prev.fixDestinationStationId) derived = 'fix';
      if (!derived) {
        resolved = { kind: 'wandered', prev };
      } else if (derived === prev.correctChoice) {
        resolved = { kind: 'arrived-correct', prev };
        needsWrite = true;
        writeKind = 'correct';
      } else {
        resolved = { kind: 'arrived-wrong', prev };
        needsWrite = true;
        writeKind = 'wrong';
      }
    }

    // Apply the visible state immediately.
    setScanResult(resolved);

    if (!needsWrite) return;

    setSubmitting(true);
    (async () => {
      try {
        const id = progressDocId(activeEvent.id, team.id, puzzle.id);
        const ref = doc(firestore, 'jumpItProgress', id);
        let nextCorrect = progress.correctStations || [];
        let nextCompleted = progress.completedAt || null;
        let nextCurrent = progress.currentStationId || '';
        let nextAttempts = progress.attempts || {};

        if (writeKind === 'first') {
          nextCurrent = station.stationId;
        } else if (writeKind === 'correct' && prev) {
          if (!nextCorrect.includes(prev.stationId)) nextCorrect = [...nextCorrect, prev.stationId];
          nextCurrent = station.stationId;
          nextAttempts = {
            ...nextAttempts,
            [prev.stationId]: (nextAttempts[prev.stationId] || 0) + 1,
          };
        } else if (writeKind === 'wrong' && prev) {
          nextAttempts = {
            ...nextAttempts,
            [prev.stationId]: (nextAttempts[prev.stationId] || 0) + 1,
          };
        }

        const requiredIds = stations
          .filter((s) => s.letter && s.letter !== 'Start')
          .map((s) => s.stationId);
        const allDone = requiredIds.length > 0 && requiredIds.every((sid) => nextCorrect.includes(sid));
        if (allDone && !nextCompleted) nextCompleted = new Date().toISOString();

        const next = {
          eventId: activeEvent.id,
          teamId: team.id,
          teamNumber: team.teamNumber || '',
          puzzleId: puzzle.id,
          correctStations: nextCorrect,
          attempts: nextAttempts,
          completedAt: nextCompleted,
          currentStationId: nextCurrent,
          updatedAt: serverTimestamp(),
        };
        await setDoc(ref, next, { merge: true });
        setProgress({ ...next, updatedAt: new Date() });
      } catch (err) {
        setFeedback('Error: ' + err.message);
      } finally {
        setSubmitting(false);
      }
    })();
  }, [stationId, station, progress, progressLoading, team, activeEvent, puzzle, stationById, submitting, stations]);

  const allLetters = stations
    .map((s) => s.letter)
    .filter((l) => l && l.toString().trim() !== '' && l !== 'Start');
  const collectedLetters = progress
    ? stations
        .filter((s) => progress.correctStations?.includes(s.stationId))
        .map((s) => s.letter)
        .filter((l) => l && l !== 'Start')
    : [];
  const isComplete = !!progress?.completedAt;
  const remainingCount = allLetters.length - collectedLetters.length;
  const solvedWord = useMemo(
    () => (isComplete ? buildSolvedWordFromStations(stations) : ''),
    [isComplete, stations]
  );

  const handlePasscodeSubmit = async (e) => {
    e.preventDefault();
    setFeedback('');
    if (!activeEvent) return setFeedback('No event found.');
    if (activeEvent.isOpen === false) return setFeedback('Bonus puzzle scoring is closed for this event.');
    if (puzzle.enabled === false) return setFeedback('This puzzle is currently disabled.');
    try {
      const { team: matched, status } = await matchTeamByPasscodeForEvent(activeEvent.id, passcodeInput);
      if (!matched) {
        return setFeedback(
          status === TEAM_PASSCODE_MAP_NO_TEAM
            ? "That passcode is on file, but there is no team for that number in this puzzle's event."
            : 'Incorrect passcode. Please try again.'
        );
      }
      const teamLite = {
        id: matched.id,
        teamNumber: matched.teamNumber,
        teamName: matched.name || matched.teamName || '',
      };
      setTeam(teamLite);
      setCachedTeam(puzzle.id, teamLite);
      setPasscodeInput('');
    } catch (err) {
      setFeedback('Error: ' + err.message);
    }
  };

  const handleSwitchTeam = () => {
    try { window.sessionStorage.removeItem(TEAM_CACHE_PREFIX + puzzle.id); } catch {}
    setTeam(null);
    setProgress(null);
    setScanResult(null);
    setFeedback('');
  };

  if (!stations || stations.length === 0) {
    return (
      <div className="jumpit-puzzle">
        <h2>{puzzle.title || 'Jump It or Fix It'}</h2>
        <p className="jumpit-warning">This puzzle has no stations configured yet.</p>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="jumpit-puzzle">
        <img src="/fresh-meet-logo.png" alt="Fresh Meet" className="jumpit-logo" />
        <h2>{puzzle.title || 'Jump It or Fix It'}</h2>
        {puzzle.tagline && <p className="jumpit-tagline">{puzzle.tagline}</p>}
        <form onSubmit={handlePasscodeSubmit} className="jumpit-passcode-form">
          <label htmlFor="jumpit-passcode">Team passcode</label>
          <input
            id="jumpit-passcode"
            type="password"
            value={passcodeInput}
            onChange={(e) => setPasscodeInput(e.target.value)}
            autoFocus
            required
          />
          <button type="submit">Enter</button>
        </form>
        {feedback && <p className="jumpit-feedback">{feedback}</p>}
        {station && (
          <p className="jumpit-hint">
            You scanned station <strong>{station.name}</strong>. Enter your team passcode to play.
          </p>
        )}
      </div>
    );
  }

  if (!stationId || !station) {
    return (
      <div className="jumpit-puzzle">
        <img src="/fresh-meet-logo.png" alt="Fresh Meet" className="jumpit-logo" />
        <h2>{puzzle.title || 'Jump It or Fix It'}</h2>
        {puzzle.tagline && <p className="jumpit-tagline">{puzzle.tagline}</p>}
        <p>Hi Team {team.teamNumber}{team.teamName ? ` (${team.teamName})` : ''}!</p>
        <ProgressDisplay
          stations={stations}
          progress={progress}
          allLetters={allLetters}
          collectedLetters={collectedLetters}
          isComplete={isComplete}
        />
        {isComplete && <JumpItCryptexPanel solvedWord={solvedWord} />}
        {!isComplete && (
          <p className="jumpit-hint">
            Scan any station's QR code to play. {remainingCount} {remainingCount === 1 ? 'letter' : 'letters'} left.
          </p>
        )}
        <button type="button" className="jumpit-link-btn" onClick={handleSwitchTeam}>Switch team</button>
      </div>
    );
  }

  // Determine which "question station" the screen should be centered on:
  // - On correct arrival or first scan: this scanned station (player advances here).
  // - On wrong arrival or wandering: keep showing the previous station's question
  //   (their pending question hasn't been answered correctly yet).
  const questionStation = (() => {
    if (!scanResult) return station;
    if (scanResult.kind === 'arrived-wrong' || scanResult.kind === 'wandered') return scanResult.prev || station;
    return station;
  })();

  const jumpDest = stationById(questionStation?.jumpDestinationStationId);
  const fixDest = stationById(questionStation?.fixDestinationStationId);
  const arrivedPrev = scanResult?.prev || null;

  // For wrong arrivals and wanders, the player has to physically backtrack.
  // We show ONLY the backtrack instruction — no question, no photo — so they
  // don't try to answer from the wrong place.
  const mustBacktrack =
    scanResult?.kind === 'arrived-wrong' || scanResult?.kind === 'wandered';

  return (
    <div className="jumpit-puzzle">
      {progressLoading && <p className="jumpit-loading">Loading…</p>}

      {isComplete && (
        <>
          <div className="jumpit-banner jumpit-banner-complete">
            You've collected every letter — use the word below at manifest.
          </div>
          <JumpItCryptexPanel solvedWord={solvedWord} />
        </>
      )}

      {!isComplete && mustBacktrack && arrivedPrev && (
        <div className="jumpit-backtrack">
          <div className="jumpit-backtrack-head">
            {scanResult.kind === 'arrived-wrong' ? 'Wrong way' : 'Off course'}
          </div>
          <div className="jumpit-backtrack-body">
            Go back to <strong>{labelFor(arrivedPrev)}</strong>
            {scanResult.kind === 'arrived-wrong' && ' and walk the other direction.'}
            {scanResult.kind === 'wandered' && ' — you haven\'t answered that question yet.'}
          </div>
          {arrivedPrev.sponsor?.image && (
            <img src={arrivedPrev.sponsor.image} alt="" className="jumpit-backtrack-logo" />
          )}
          {arrivedPrev.questionImage && (
            <div className="jumpit-backtrack-question">
              <div className="jumpit-backtrack-question-head">The question you missed:</div>
              <img
                src={arrivedPrev.questionImage}
                alt={arrivedPrev.name}
                className="jumpit-backtrack-photo"
              />
              <div className="jumpit-backtrack-stationname">{arrivedPrev.name}</div>
            </div>
          )}
          {scanResult.kind === 'arrived-wrong' && arrivedPrev.wrongMessage && (
            <p className="jumpit-backtrack-msg">{arrivedPrev.wrongMessage}</p>
          )}
          {arrivedPrev.explanation && (
            <div className="jumpit-backtrack-why">
              <div className="jumpit-backtrack-why-head">Why:</div>
              <p>{arrivedPrev.explanation}</p>
              {arrivedPrev.sourceUrl && (
                <p className="jumpit-backtrack-source">
                  <a href={arrivedPrev.sourceUrl} target="_blank" rel="noopener noreferrer">
                    {arrivedPrev.sourceLabel || 'Read more'} →
                  </a>
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!isComplete && !mustBacktrack && questionStation && (
        <>
          {/* Big celebratory success banner with mini photo of what they just answered */}
          {scanResult?.kind === 'arrived-correct' && arrivedPrev && (
            <div className="jumpit-success">
              <div className="jumpit-success-head">
                <div className="jumpit-success-yes">YES — that's right!</div>
                {arrivedPrev.letter && arrivedPrev.letter !== 'Start' && (
                  <div className="jumpit-success-letter">
                    You earned letter <strong>{arrivedPrev.letter}</strong>
                  </div>
                )}
                {arrivedPrev.letter === 'Start' && (
                  <div className="jumpit-success-letter">Warm-up correct — now the real puzzle begins.</div>
                )}
              </div>
              <div className="jumpit-success-body">
                {arrivedPrev.questionImage && (
                  <img
                    src={arrivedPrev.questionImage}
                    alt={arrivedPrev.name}
                    className="jumpit-success-mini"
                  />
                )}
                <div className="jumpit-success-text">
                  <div className="jumpit-success-stationname">{arrivedPrev.name}</div>
                  {arrivedPrev.explanation && (
                    <p className="jumpit-success-why">{arrivedPrev.explanation}</p>
                  )}
                  {arrivedPrev.sourceUrl && (
                    <p className="jumpit-success-source">
                      <a href={arrivedPrev.sourceUrl} target="_blank" rel="noopener noreferrer">
                        {arrivedPrev.sourceLabel || 'Read more'} →
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* First-scan onboarding card so players know what they're doing */}
          {scanResult?.kind === 'first' && (
            <div className="jumpit-howto">
              <div className="jumpit-howto-head">How this works</div>
              <ol className="jumpit-howto-list">
                <li>Look at the photo below — is the gear good to <em>jump it</em>, or does it need a <em>fix</em>?</li>
                <li>Decide, then <strong>walk to the station shown below</strong> for your answer.</li>
                <li>Scan that station's QR. If you're right you collect a letter; if you're wrong, walk back and try the other way.</li>
                <li>Collect every letter to spell the word, then bring it to manifest.</li>
              </ol>
            </div>
          )}

          {/* "Next question" header to clearly separate from success banner */}
          {scanResult?.kind === 'arrived-correct' && (
            <div className="jumpit-next-header">Now — inspect this gear:</div>
          )}
          {scanResult?.kind === 'first' && (
            <div className="jumpit-next-header">Inspect this gear:</div>
          )}

          {/* Station name intentionally hidden on the active question so
              descriptive names like "A-01 Botched Leg Strap" don't give the
              answer away. Players see the photo + prompt only. */}

          {/* PRIMARY: big photo */}
          {questionStation.questionImage && (
            <img
              src={questionStation.questionImage}
              alt={questionStation.name}
              className="jumpit-question-img jumpit-question-img--primary"
            />
          )}

          {/* Prompt as compact caption */}
          {questionStation.prompt && (
            <p className="jumpit-prompt jumpit-prompt--compact">{questionStation.prompt}</p>
          )}

          {/* Walk instructions */}
          <div className="jumpit-walk-row">
            <div className="jumpit-walk jumpit-walk-jump">
              <div className="jumpit-walk-head">If JUMP IT →</div>
              <div className="jumpit-walk-dest">walk to<br/><strong>{labelFor(jumpDest) || '(not configured)'}</strong></div>
              {jumpDest?.sponsor?.image && (
                <img src={jumpDest.sponsor.image} alt="" className="jumpit-walk-logo" />
              )}
            </div>
            <div className="jumpit-walk jumpit-walk-fix">
              <div className="jumpit-walk-head">If FIX IT →</div>
              <div className="jumpit-walk-dest">walk to<br/><strong>{labelFor(fixDest) || '(not configured)'}</strong></div>
              {fixDest?.sponsor?.image && (
                <img src={fixDest.sponsor.image} alt="" className="jumpit-walk-logo" />
              )}
            </div>
          </div>
          <p className="jumpit-hint">Scan the QR at the station you walked to.</p>
        </>
      )}

      {/* Letters strip at the bottom — compact reference, no map */}
      <ProgressDisplay
        stations={stations}
        progress={progress}
        allLetters={allLetters}
        collectedLetters={collectedLetters}
        isComplete={isComplete}
      />

      <button type="button" className="jumpit-link-btn" onClick={handleSwitchTeam}>Switch team</button>
    </div>
  );
};

const ProgressDisplay = ({ stations, progress, allLetters, collectedLetters, isComplete }) => {
  if (!progress) return null;
  const countsTowardWord = (s) => s.letter && String(s.letter).trim() !== '' && s.letter !== 'Start';
  return (
    <div className="jumpit-progress">
      <h3 className="jumpit-progress-heading">Letters</h3>
      <div className="jumpit-letters-row" aria-label="Letters collected so far">
        {stations.filter(countsTowardWord).map((s) => {
          const got = progress.correctStations?.includes(s.stationId);
          return (
            <span
              key={s.stationId}
              className={`jumpit-letter ${got ? 'jumpit-letter-got' : 'jumpit-letter-missing'}`}
              title={got ? `Letter ${s.letter}` : 'Locked'}
            >
              {got ? s.letter : '_'}
            </span>
          );
        })}
      </div>
      <div className="jumpit-progress-text">
        {isComplete
          ? 'All letters found — see your word below.'
          : `${collectedLetters.length} of ${allLetters.length} letters collected`}
      </div>
    </div>
  );
};

export default JumpItOrFixItPuzzle;
